import {
    ChangeDetectorRef,
    Component,
    Injector,
    Input,
    OnInit,
    ViewEncapsulation
} from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { SessionService, User } from "@edl-development/edl-common";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { EdllNotesComponent } from "src/app/components/edl-notes/edl-notes.component";
import { CplA48UserSubmissionDetail } from "src/app/model/a48/cpl-a48-user-submission-detail.model";
import { TimeTrackingFormService } from "src/app/time-tracking/services/time-tracking-form/time-tracking-form.service";
import { Act48UserSubmissionDetailsModalComponent } from "../../modals/a48/a48-user-details-confirm-modal.component";
import { EdlModalEvidence } from "../../modals/edl-evidence-modal.component";
import { NavigationItemId } from "../../modals/edl-modal-details/navigation-item-id.enum";
import { NavigationItem } from "../../modals/edl-modal-details/navigation-item.model";
import { NavigationSection } from "../../modals/edl-modal-details/navigation-section.model";
import { CplRequestItem } from "../../model/cpl-request-item.model";
import { CplUserRequest } from "../../model/cpl-user-request.model";
import { RequestUserStatus } from '../../model/enum/request-user-status.enum';
import { AccessRoleHelper } from "../../model/helpers/access-role.helper";
import { CompleteUserFormComponent } from "../../questionnaires/user-form/complete-user-form.component";
import { QuestionnaireService } from "../../services/questionnaire.service";
import { RequestHelpersService } from "../../services/request-helpers/request-helpers.service";
import { UserRequestService } from "../../services/user-request.service";
import { NoteTypes } from "../../supervision-management/compliance-items/compliance-item/enums/note-types.enum";
import { NotesToInclude } from "../../supervision-management/compliance-items/compliance-item/enums/notes-to-include.enum";
import { UserRequestDetailsSummary } from "../components/user-request-details/user-request-summary/user-request-summary.component";
import { RequestVerificationStatus } from "../enums/verification-status.enum";
import { UserRequestFlexDetailsComponent } from "./user-request-flex-details/user-request-flex-details.component";
import { VerifyRequestModalComponent } from "./verify-request-modal/verify-request-modal.component";

@Component({
    selector: "request-details",
    templateUrl: "./sm-user-request-details-modal.component.html",
    encapsulation: ViewEncapsulation.None
})
export class UserRequestDetailsModal implements OnInit {
    @Input() userRequest: CplUserRequest;
    @Input() request: CplRequestItem;

    isReadOnly: boolean;
    requestDetails: any;
    navigationSections: Array<NavigationSection>;
    isVerifier: boolean;
    user: User;

    navigationItemId = NavigationItemId.DETAILS;
    form: UntypedFormGroup;

    constructor(
        public activeModal: NgbActiveModal,
        private userRequestService: UserRequestService,
        private questionnaireService: QuestionnaireService,
        private sessionService: SessionService,
        private requestHelpersService: RequestHelpersService,
        private timeTrackingFormService: TimeTrackingFormService,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit() {
        this.isReadOnly = !this.requestHelpersService.isRequestItemActive(this.request);
        this.user = this.sessionService.user;
        this.SetVerifier();
        this.userRequestService
            .getUserRequest(this.userRequest.UserRequestID)
            .subscribe((response: any) => {
                this.requestDetails = response;
                this.loadNavigationItems();
            });

        this.loadNavigationItems();
    }

    updateRequestData(newRequestItem: CplRequestItem) {
        // Update the request data
        this.request = newRequestItem;

        // Clear existing navigation sections to force complete rebuild
        this.navigationSections = [];

        // Re-fetch user request details with the new data first
        this.userRequestService
            .getUserRequest(this.userRequest.UserRequestID)
            .subscribe((response: any) => {
                this.requestDetails = response;

                // Now reload navigation items with fresh request details
                this.loadNavigationItems();

                // Force change detection to ensure UI updates
                this.cdr.detectChanges();

                // Force the Details section to be reselected to trigger component refresh
                setTimeout(() => {
                    this.navigationItemId = NavigationItemId.DETAILS;
                    this.cdr.detectChanges();
                }, 0);
            });
    }

    loadNavigationItems() {
        this.navigationSections = Array<NavigationSection>();

        // Summary Section
        this.pushSection('Summary');
        this.pushItem(0, new NavigationItem(NavigationItemId.DETAILS, 'Details', UserRequestDetailsSummary, { CplUserRequest: this.userRequest, CplRequestItem: this.request }, this.loadSummary));

        if (AccessRoleHelper.isLimitedSupervisor(this.user.AccessRoleID)) {
            this.pushItem(0, new NavigationItem(NavigationItemId.NOTES, 'Notes', EdllNotesComponent, { CplUserRequest: this.userRequest, CplRequestItem: this.request }, this.loadNotes));
        }

        if (this.request && this.request.Act48Detail && this.request.Act48Detail.Applicable) {
            this.pushItem(0, new NavigationItem(NavigationItemId.ACT48, 'Act 48', Act48UserSubmissionDetailsModalComponent, { CplUserRequest: this.userRequest, CplRequestItem: this.request }, this.loadAct48Info));
        }

        if (this.request && this.request.FlexDetails && this.request.FlexDetails.Applicable) {
            this.pushItem(0, new NavigationItem(NavigationItemId.FLEX, 'Flex', UserRequestFlexDetailsComponent, { form: this.form?.get('FlexDetails'), userRequest: this.userRequest, requestItem: this.request }, this.loadFlexDetails));
        }

        if (this.isVerifier && !this.isReadOnly) {
            this.pushItem(0, new NavigationItem(NavigationItemId.APPROVAL, 'Approval', VerifyRequestModalComponent, { CplUserRequest: this.userRequest, CplRequestItem: this.request }, this.loadApproval));
        }

        // Request Details Seciton
        if (this.requestDetails) {
            this.pushSection('Request Details');

            this.requestDetails.Questionnaires.forEach(qfm => {
                const navigationItemId = this.getUserFormNavigationItemId(false, qfm.UserFormId);
                this.pushItem(1, new NavigationItem(navigationItemId, `Questionnaire - ${qfm.FormTitle}`, CompleteUserFormComponent, qfm, this.loadQuestionaireData, qfm.FormRequired, { isFollowup: false }));
            });

            this.pushItem(1, new NavigationItem(NavigationItemId.EVIDENCE, 'Evidence', EdlModalEvidence, { CplUserRequest: this.userRequest, CplRequestItem: this.request }, this.loadEvidence, this.request.EvidenceRequired));
        }


        // Follow-Up Details Section
        if (this.requestDetails && this.requestDetails.Followup && this.requestDetails.Followup.FollowupID != null) {
            this.pushSection('Follow-Up Details');

            this.requestDetails.Followup.Questionnaires.forEach(qfm => {
                const navigationItemId = this.getUserFormNavigationItemId(true, qfm.UserFormId);
                this.pushItem(2, new NavigationItem(navigationItemId, `Questionnaire - ${qfm.FormTitle}`, CompleteUserFormComponent, qfm, this.loadQuestionaireData, qfm.FormRequired, { isFollowup: true }));
            });

            this.pushItem(2, new NavigationItem(NavigationItemId.FOLLOW_UP_EVIDENCE, 'Evidence', EdlModalEvidence, { CplUserRequest: this.requestDetails, CplRequestItem: this.request }, this.loadFUEvidence, this.request.FollowupEvidenceRequired));
        }
    }

    getUserFormNavigationItemId(isFollowup: boolean, userFormId: string) {
        const prefix = isFollowup ? NavigationItemId.FOLLOW_UP_QUESTIONNAIRE : NavigationItemId.QUESTIONNAIRE;
        return prefix + '-' + userFormId;
    }

    pushSection(title: string) {
        this.navigationSections.push(new NavigationSection(title, new Array<NavigationItem>()));
    }

    pushItem(sectionIndex: number, item: NavigationItem) {
        this.navigationSections[sectionIndex].navigationItems.push(item);
    }


    loadNotes = (ref: any, _args: any) => {
        const injector = Injector.create({ providers: [], parent: ref.container.parentInjector });
        const componentRef = ref.container.createComponent(ref.factory, 0, injector);

        componentRef.instance.typeOfNote = NoteTypes.USER_REQUEST;
        componentRef.instance.typeId = ref.data.CplUserRequest.UserRequestID;
        componentRef.instance.notesToInclude = NotesToInclude.BOTH;
        componentRef.instance.isReadOnly = ref.parent.isReadOnly;
        ref.parent.busy = false;
    }


    loadAct48Info = (ref: any, _args: any) => {
        const injector = Injector.create({ providers: [], parent: ref.container.parentInjector });
        const componentRef = ref.container.createComponent(ref.factory, 0, injector);

        componentRef.instance.AdminUI = true;
        componentRef.instance.ShowClose = false;
        componentRef.instance.TypeId = ref.data.CplUserRequest.UserRequestID;
        componentRef.instance.Type = 1;
        componentRef.instance.userId = ref.data.CplUserRequest.UserID;
        componentRef.instance.isReadOnly = ref.parent.isReadOnly;
        componentRef.instance.isItemComplete = this.requestHelpersService.isFinalUserStatus(ref.data.CplUserRequest.UserStatus);

        componentRef.instance.onSubmissionComplete
            .subscribe(({ success, details }: { success: boolean, details: CplA48UserSubmissionDetail }) => {
                if (success) {
                    this.userRequest.Act48UserSubmissionDetails = details;
                }

                ref.parent.onSubmissionComplete(success);
            });

        ref.parent.busy = false;
    }

    loadFlexDetails = (ref: any, _args: any) => {
        const injector = Injector.create({ providers: [], parent: ref.container.parentInjector });
        const componentRef = ref.container.createComponent(ref.factory, 0, injector);

        if (!this.form) {
            this.createFlexDetailsForm();
        }

        componentRef.instance.form = this.form?.get('FlexDetails');
        componentRef.instance.userRequest = this.userRequest;
        componentRef.instance.requestItem = this.request;
        componentRef.instance.isReadOnly = this.isReadOnly;

        componentRef.instance.onSaveComplete
            .subscribe(({ success }: { success: boolean }) => {
                if (success) {
                    //ensure refresh
                    this.createFlexDetailsForm();
                }
                ref.parent.onSubmissionComplete(success);
            });
        ref.parent.busy = false;
    }

    loadApproval = (ref: any, _args: any) => {
        const injector = Injector.create({ providers: [], parent: ref.container.parentInjector });
        const componentRef = ref.container.createComponent(ref.factory, 0, injector);

        componentRef.instance.userRequest = ref.data.CplUserRequest;
        componentRef.instance.requestItem = ref.data.CplRequestItem;
        componentRef.instance.action = RequestVerificationStatus.APPROVED;
        componentRef.instance.canClose = false;
        componentRef.instance.onSubmissionComplete.subscribe((shouldReload: boolean) => {
            ref.parent.onSubmissionComplete(shouldReload, true)
        });

        ref.parent.busy = false;
    }

    loadSummary = (ref: any, _args: any) => {
        const injector = Injector.create({ providers: [], parent: ref.container.parentInjector });
        const componentRef = ref.container.createComponent(ref.factory, 0, injector);

        componentRef.instance.userRequest = ref.data.CplUserRequest;
        componentRef.instance.request = ref.data.CplRequestItem;
        componentRef.instance.onSubmissionComplete.subscribe(() => {
            ref.parent.onSubmissionComplete(false, true)
        });
        ref.parent.busy = false;
    }

    loadEvidence = (ref: any, _args: any) => {
        const injector = Injector.create({
            providers: [],
            parent: ref.container.parentInjector
        });
        const componentRef = ref.container.createComponent(
            ref.factory,
            0,
            injector
        );

        const UserRequest = this.userRequest;
        const UserRequestComplete = this.requestHelpersService.isFinalUserStatus(UserRequest.UserStatus);

        componentRef.instance.fileType = 6;
        componentRef.instance.fileTypeId = ref.data.CplUserRequest.UserRequestID;
        componentRef.instance.isReadOnly = UserRequestComplete || ref.parent.isReadOnly;
        componentRef.instance.isDeleteDisabled = ref.data.CplUserRequest.UserID === this.sessionService.user.UserID;
        componentRef.instance.canClose = false;
        ref.parent.busy = false;
    }

    loadFUEvidence = (ref: any, _args: any) => {
        const injector = Injector.create({
            providers: [],
            parent: ref.container.parentInjector
        });
        const componentRef = ref.container.createComponent(
            ref.factory,
            0,
            injector
        );

        const UserRequest = this.userRequest;
        const UserRequestComplete = this.requestHelpersService.isFinalUserStatus(UserRequest.UserStatus);

        componentRef.instance.fileType = 7;
        componentRef.instance.fileTypeId = ref.data.CplUserRequest.Followup.FollowupID;
        componentRef.instance.isReadOnly = UserRequestComplete || ref.parent.isReadOnly;
        componentRef.instance.isDeleteDisabled = ref.data.CplUserRequest.UserID === this.sessionService.user.UserID;
        componentRef.instance.canClose = false;
        ref.parent.busy = false;
    }

    loadQuestionaireData = (ref: any, _args: any) => {

        this.questionnaireService
            .getUserForm(ref.data.UserFormId)
            .subscribe((response: any) => {
                const injector = Injector.create({
                    providers: [],
                    parent: ref.container.parentInjector
                });
                const componentRef = ref.container.createComponent(
                    ref.factory,
                    0,
                    injector
                );

                let UserRequest = this.userRequest;
                let UserRequestComplete: Boolean = this.requestHelpersService.isFinalUserStatus(UserRequest.UserStatus);
                const UserRequestPendingSubmission = this.requestHelpersService.isPendingSubmission(UserRequest.UserStatus);
                const UserRequestFollowUpSubmitted = UserRequest.UserStatus === RequestUserStatus.FollowupSubmitted;

                const isFollowup = _args.isFollowup;

                componentRef.instance.userFormDetails = response;
                componentRef.instance.masterComplete = UserRequestComplete || ref.parent.isReadOnly || (isFollowup ? !UserRequestFollowUpSubmitted : UserRequestPendingSubmission);
                componentRef.instance.canClose = false;
                ref.parent.busy = false;
            });
    }

    SetVerifier() {
        this.isVerifier = false;

        // Check main verification stages
        if (this.request?.VerificationStages?.length > 0) {
            for (var i = 0; i < this.request.VerificationStages.length; i++) {
                if (this.request.VerificationStages[i]?.Approvers?.filter(user => user.UserID == this.user.UserID).length > 0) {
                    this.isVerifier = true;
                    return;
                }
            }
        }

        // Check followup verification stages
        if (this.request?.FollowupVerificationStages?.length > 0) {
            for (var i = 0; i < this.request.FollowupVerificationStages.length; i++) {
                if (this.request.FollowupVerificationStages[i]?.Approvers?.filter(user => user.UserID == this.user.UserID).length > 0) {
                    this.isVerifier = true;
                    return;
                }
            }
        }
    }

    private createFlexDetailsForm() {
        // Create form using the same pattern as compliance-item-form
        const { timeTrackingForm, timeTrackingSubscription } = this.timeTrackingFormService.buildForm(
            this.userRequest.FlexDetails,
            false // isComplianceItem
        );

        this.form = new UntypedFormGroup({
            FlexDetails: timeTrackingForm
        });
    }
}
